import React, { useState } from 'react';
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { applyMiddleware } from 'helpers/redux_dev_tools';
import thunk from 'redux-thunk';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import NewPropertyFromExisting from './new_listing_modal/new_property_from_existing';
import NewPropertyFromScratch from './new_listing_modal/new_property_from_scratch';
import NewPropertyFromUpload from './new_listing_modal/new_property_from_upload';
import IntroStep from './new_listing_modal/intro_step';
import reducers from './new_listing_modal/reducers';
import { normalizeBrokerOptions } from './new_listing_modal/helpers/normalizers';
import { countryListPropType, brokerTuple } from './new_listing_modal/prop_types';

import { post } from 'helpers/api';
export default function NewListingModalReduxWrapper({
  allowUploadDoc,
  countryList,
  crmAccess,
  defaultBrokerId,
  edgeCrmAccess,
  hasInsights,
  maxBrokersLimit,
  brokerOptionTuples
}) {

  const initialStoreData = {
    brokerSelectOptions: normalizeBrokerOptions(brokerOptionTuples), // readonly
    newPropertyFromScratchLocation: {
      country: countryList.find(country => country.name === 'United States') || countryList[0]
    }
  };
  const store = createStore(reducers, initialStoreData, applyMiddleware(thunk));
  if (module.hot) {
    module.hot.accept('./new_listing_modal/reducers/index', () => {
      store.replaceReducer(require('./new_listing_modal/reducers/index'));
    });
  }

  return (
    <Provider store={store}>
      <NewListingModal
        allowUploadDoc={allowUploadDoc}
        countryList={countryList}
        crmAccess={crmAccess}
        defaultBrokerId={defaultBrokerId}
        edgeCrmAccess={edgeCrmAccess}
        hasInsights={hasInsights}
        maxBrokersLimit={maxBrokersLimit}
      />
    </Provider>
  );
}

export function NewListingModal({
  allowUploadDoc,
  countryList,
  crmAccess,
  defaultBrokerId,
  edgeCrmAccess,
  hasInsights,
  maxBrokersLimit,
}) {
  const MODAL_STEPS = {
    INTRO: 'intro',
    FROM_SCRATCH: 'from_scratch',
    FROM_PROPERTY: 'from_property',
    FROM_UPLOAD: 'from_upload'
  };

  const hasAnyCrmAccess = crmAccess || edgeCrmAccess;

  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const onFromExistingSelected = hasAnyCrmAccess ? () => setStep(MODAL_STEPS.FROM_PROPERTY) : null;
  const onFromScratchSelected = () => setStep(MODAL_STEPS.FROM_SCRATCH);
  const onUploadDocSelected = allowUploadDoc ? () => setStep(MODAL_STEPS.FROM_UPLOAD) : null;

  const initialStep = (onFromExistingSelected || onUploadDocSelected) ? MODAL_STEPS.INTRO : MODAL_STEPS.FROM_SCRATCH;
  const [step, setStep] = useState(initialStep);

  const hideModal = () => {
    setStep(initialStep);
    setShow(false);
    setHasErrors(false);
  };

  const brokersAttributes = (brokerIds) => {
    if (defaultBrokerId) {
      return { '0': { primary: true, user_id: defaultBrokerId } };
    }

    return brokerIds.map((brokerId, index) => {
      return ({
        primary: index === 0,
        sortOrder: index,
        userId: brokerId
      });
    });
  };

  const save = ({
    edgeResearchPropertyId,
    researchPropertyId,
    brokerIds = [{}],
    propertyParams = {},
    researchPropertyParams = {},
    aiPopulatedFields = [],
    insightsId
  }) => {
    setSaving(true);
    setErrorMessages({});
    setHasErrors(false);
    const params = {
      edgeResearchPropertyId: edgeResearchPropertyId,
      researchPropertyId: researchPropertyId,
      property: {
        draft: true,
        saleAgreementAttributes: { proposal: true },
        leaseAgreementAttributes: { proposal: true },
        projectsUsersAttributes: brokersAttributes(brokerIds),
        ...propertyParams,
        research_property_attributes: researchPropertyParams
      },
    };

    post('/properties', params)
      .then(({ path }) => {
        const url = new URL(path, window.location.origin);
        if (insightsId) {
          url.searchParams.set('address_correction', insightsId);
        }
        if (aiPopulatedFields && aiPopulatedFields.length > 0) {
          url.searchParams.set('ai_populated_fields', aiPopulatedFields.join(','));
        }
        window.location = url.toString();
      })
      .catch((error) => {
        setSaving(false);
        setHasErrors(true);
        setErrorMessages(error);
      });
  };

  const newPropertyScreen = (NewPropertyScreen, extraProps = {}) => {
    // leave unset to omit back button
    const gotoInitialStep = initialStep == MODAL_STEPS.INTRO ? (() => {
      setStep(MODAL_STEPS.INTRO);
      setHasErrors(false);
    }) : null;

    const newListingProps = {
      gotoPrevStep: gotoInitialStep,
      saving,
      showBrokerSelect: !defaultBrokerId,
      onSave: save,
      hasErrors,
      errorMessages,
      maxBrokersLimit,
      ...extraProps
    };

    return <NewPropertyScreen {...newListingProps} />;
  };

  const modalContent = () => {
    if (step === MODAL_STEPS.INTRO) {
      return (
        <IntroStep
          onFromExistingSelected={onFromExistingSelected}
          onFromScratchSelected={onFromScratchSelected}
          onUploadDocSelected={onUploadDocSelected}
        />
      );
    } else if (step === MODAL_STEPS.FROM_PROPERTY) {
      return newPropertyScreen(NewPropertyFromExisting, { edgeCrmAccess });
    } else if (step === MODAL_STEPS.FROM_SCRATCH) {
      return newPropertyScreen(NewPropertyFromScratch, {
        countryList,
        hasInsights
      });
    } else if (step === MODAL_STEPS.FROM_UPLOAD) {
      return newPropertyScreen(NewPropertyFromUpload, { });
    }
  };

  return (
    <React.Fragment>
      <button className="w-100 btn btn-primary" name="new-listing-button" type="button" onClick={() => setShow(true)}>
        New Listing
      </button>
      <Modal bsSize="sm" show={show} style={{ '--animate-duration': '0.3s' }} onHide={hideModal}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            classNames={{
              enter: 'animate__animated',
              enterActive: 'animate__fadeIn',
              exit: 'animate__animated',
              exitActive: 'animate__fadeOut'
            }}
            key={step}
            timeout={500}
          >
            {modalContent()}
          </CSSTransition>
        </SwitchTransition>
      </Modal>
    </React.Fragment>
  );
}

const sharedProps = {
  allowUploadDoc: PropTypes.bool,
  countryList: countryListPropType.isRequired,
  crmAccess: PropTypes.bool.isRequired,
  defaultBrokerId: PropTypes.number,
  edgeCrmAccess: PropTypes.bool,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired
};

NewListingModalReduxWrapper.propTypes = {
  ...sharedProps,
  brokerOptionTuples: PropTypes.arrayOf(brokerTuple).isRequired,
};

NewListingModal.propTypes = sharedProps;

